'use client';

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/product/ProductCard';
import { PageContainer } from '@/components/layout/PageContainer';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/button';
import type { Product } from '@/types';

type SortOption = 'newest' | 'price_asc' | 'price_desc';

const PAGE_SIZE = 20;

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sort, setSort] = useState<SortOption>('newest');
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadProducts = useCallback(async (currentOffset: number = 0, append: boolean = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await api.products.getProducts({
        limit: PAGE_SIZE,
        offset: currentOffset,
      });
      
      if (append) {
        setProducts((prev) => [...prev, ...response.items]);
      } else {
        setProducts(response.items);
      }
      setTotal(response.total);
      setOffset(currentOffset);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load products'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadProducts(0, false);
  }, [loadProducts]);

  const handleLoadMore = () => {
    const nextOffset = offset + PAGE_SIZE;
    if (nextOffset < total) {
      loadProducts(nextOffset, true);
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    if (sort === 'price_asc') {
      return (a.salePrice || a.price) - (b.salePrice || b.price);
    } else if (sort === 'price_desc') {
      return (b.salePrice || b.price) - (a.salePrice || a.price);
    }
    // newest: keep original order (products from API are already sorted by newest)
    return 0;
  });

  const hasMore = offset + products.length < total;

  if (loading) {
    return (
      <PageContainer>
        <h1 className="text-3xl font-bold mb-8 text-gray-900">Бүх бараа</h1>
        <LoadingState text="Бараа ачааллаж байна..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <h1 className="text-3xl font-bold mb-8 text-gray-900">Бүх бараа</h1>
        <ErrorState
          title="Бараа ачааллахад алдаа гарлаа"
          message="Дахин оролдоно уу."
          onRetry={() => loadProducts(0, false)}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Бүх бараа</h1>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="newest">Шинэ</option>
          <option value="price_asc">Үнэ: Багаас их рүү</option>
          <option value="price_desc">Үнэ: Ихээс бага руу</option>
        </select>
      </div>

      {sortedProducts.length === 0 ? (
        <EmptyState
          title="Бараа байхгүй байна"
          description="Дараа дахин шалгана уу."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                variant="outline"
              >
                {loadingMore ? 'Ачааллаж байна...' : `Цааш ачаалах (${total - (offset + products.length)} үлдсэн)`}
              </Button>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}

